import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { deleteFile } from "@/services/api/files";
import { toast } from "@/shared/lib/toast";
import { galleryKeys } from "./gallery/useGallery";
import type { FileItem } from "../types";

interface UseFileDeleteInput {
  file: Pick<FileItem, "id">;
  categoryId?: string | number;
  onDeleteSuccess?: () => void;
}

export function useFileDelete({
  file,
  categoryId,
  onDeleteSuccess,
}: UseFileDeleteInput) {
  const [isDeleting, setIsDeleting] = useState(false);
  const queryClient = useQueryClient();

  const deleteFileHandler = useCallback(async () => {
    if (!file?.id) return;

    setIsDeleting(true);
    try {
      await deleteFile(file.id);

      if (categoryId) {
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey;
            return (
              Array.isArray(queryKey) &&
              queryKey.length >= 3 &&
              queryKey[0] === "gallery" &&
              queryKey[1] === "list" &&
              (queryKey[2] === categoryId ||
                String(queryKey[2]) === String(categoryId))
            );
          },
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: galleryKeys.lists(),
        });
      }

      toast.success("File deleted", "The file has been successfully deleted.");

      onDeleteSuccess?.();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      toast.error(
        "Delete failed",
        `We couldn't delete this file. ${errorMessage}`
      );
    } finally {
      setIsDeleting(false);
    }
  }, [file.id, categoryId, queryClient, onDeleteSuccess]);

  return { isDeleting, deleteFile: deleteFileHandler };
}
